import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ImageIcon, CheckCircle2, XCircle } from "lucide-react";

export default function AdminImageReviews() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/product-images?status=${status}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [status]);

  const approve = async (img) => { try { await api.post(`/admin/product-images/${img.id}/approve`); toast.success("Image approved & published"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("A rejection reason is required");
    setBusy(true);
    try { await api.post(`/admin/product-images/${rejectFor.id}/reject`, { reason: reason.trim() }); toast.success("Image rejected — reason sent to brand"); setRejectFor(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const TABS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]];

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Images Pending Review</h1>
        <p className="text-sm text-brown/70 mt-1">Approve or reject brand-submitted product images before they go live.</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`img-tab-${v}`} className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${status === v ? "bg-forest text-cream border-forest" : "bg-white text-brown border-sage/50 hover:border-forest"}`}>
            {l}{v !== "all" && counts[v] ? ` (${counts[v]})` : ""}
          </button>
        ))}
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
        items.length === 0 ? <div className="text-center py-16 text-muted-foreground"><ImageIcon className="w-10 h-10 mx-auto mb-2 text-sage" />No {status !== "all" ? status : ""} images.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="image-review-grid">
          {items.map((img) => (
            <div key={img.id} className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid={`image-card-${img.id}`}>
              <div className="h-44 bg-sand"><img src={img.url} alt={img.slot} className="w-full h-full object-cover" /></div>
              <div className="p-3">
                <p className="text-sm font-500 text-forest truncate">{img.product_name}</p>
                <p className="text-xs text-brown/60">{img.brand_name} • <span className="text-olive">{img.slot}</span></p>
                {img.status === "rejected" && <p className="text-[11px] text-red-600 mt-1">Reason: {img.reject_reason}</p>}
                {img.status === "approved" && <p className="text-[11px] text-emerald-700 mt-1">Published to storefront</p>}
                {img.status === "pending" && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => approve(img)} data-testid={`approve-image-${img.id}`} className="rounded-full bg-forest hover:bg-earth text-cream flex-1"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => { setRejectFor(img); setReason(""); }} data-testid={`reject-image-${img.id}`} className="rounded-full border-terracotta text-terracotta flex-1"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Reject Image</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-brown/70">{rejectFor?.product_name} — {rejectFor?.slot}</p>
            <Label className="text-sm">Reason (visible to the brand) *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-xl bg-white" placeholder="e.g. Image is blurry / wrong product / low resolution" data-testid="reject-reason-input" />
          </div>
          <DialogFooter><Button onClick={doReject} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white" data-testid="confirm-reject-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject & Notify Brand"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
